import * as React from "react";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import useIngestionActions from "../hooks/use-ingestion-actions";
import { FormErrors, SftpIngestion, SftpIngestionFormValues } from "../types";
import { sftpFormValuesFrom } from "../utils/mappers";
import { validateSftpIngestion } from "../utils/validation";

type TextFieldName = Exclude<
  keyof SftpIngestionFormValues,
  "enabled" | "transferSubDirectory" | "upload"
>;
type FlagField = "enabled" | "transferSubDirectory" | "upload";

const CONNECTION_FIELDS: {
  name: TextFieldName;
  label: string;
  type?: string;
}[] = [
  { name: "hostName", label: "Host name" },
  { name: "ipAddress", label: "IP address" },
  { name: "portNumber", label: "Port" },
  { name: "userName", label: "User name" },
  { name: "password", label: "Password", type: "password" },
  { name: "hostKeyFingerPrint", label: "Host key fingerprint" },
];

export interface SftpIngestionDialogProps {
  open: boolean;
  clientId: number;
  /** The ingestion to edit; omitted when creating. */
  ingestion?: SftpIngestion;
  /** `saved` is true when the ingestion was created or updated. */
  onClose: (saved: boolean) => void;
}

const SftpIngestionDialog = ({
  open,
  clientId,
  ingestion,
  onClose,
}: SftpIngestionDialogProps) => {
  const { saveSftpIngestion } = useIngestionActions();
  const [values, setValues] = React.useState<SftpIngestionFormValues>(() =>
    sftpFormValuesFrom(ingestion),
  );
  const [errors, setErrors] = React.useState<
    FormErrors<SftpIngestionFormValues>
  >({});
  const [isSaving, setIsSaving] = React.useState(false);
  const isEdit = ingestion !== undefined;

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setValues((previous) => ({ ...previous, [name]: value }));
  };

  const handleFlagChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setValues((previous) => ({ ...previous, [name as FlagField]: checked }));
  };

  const handleSubmit = async () => {
    const validationErrors = validateSftpIngestion(values);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    setIsSaving(true);
    const saved = await saveSftpIngestion(clientId, ingestion, values);
    setIsSaving(false);
    if (saved) {
      onClose(true);
    }
  };

  const textField = (
    name: TextFieldName,
    label: string,
    options: { required?: boolean; type?: string; helperText?: string } = {},
  ) => (
    <TextField
      key={name}
      id={`sftp-ingestion-${name}`}
      name={name}
      label={label}
      type={options.type}
      required={options.required}
      value={values[name]}
      onChange={handleTextChange}
      error={errors[name] !== undefined}
      helperText={errors[name] ?? options.helperText}
      autoComplete={options.type === "password" ? "new-password" : "off"}
      size="small"
      fullWidth
    />
  );

  const flag = (name: FlagField, label: string) => (
    <FormControlLabel
      control={
        <Checkbox
          name={name}
          checked={values[name]}
          onChange={handleFlagChange}
        />
      }
      label={label}
    />
  );

  return (
    <Dialog
      open={open}
      onClose={() => onClose(false)}
      maxWidth="sm"
      fullWidth
      aria-labelledby="sftp-ingestion-dialog-title"
    >
      <DialogTitle
        id="sftp-ingestion-dialog-title"
        sx={{ borderBottom: 2, mb: 2, borderColor: "divider" }}
      >
        {isEdit ? "Edit SFTP Ingestion" : "Add SFTP Ingestion"}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {textField("description", "Description", { required: true })}
          {textField("sourceDirectory", "Source directory", {
            required: true,
            helperText: "Directory on the SFTP server to collect files from",
          })}
          {textField("startDirectory", "Start directory", {
            helperText: isEdit
              ? "Base directory on the server"
              : "Leave blank to use the existing server's start directory",
          })}
          <FormGroup row>
            {flag("enabled", "Enabled")}
            {flag("transferSubDirectory", "Transfer sub-directories")}
            {flag("upload", "Upload")}
          </FormGroup>
          <Accordion
            disableGutters
            variant="outlined"
            slotProps={{ transition: { unmountOnExit: true } }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              id="sftp-ingestion-connection-header"
              aria-controls="sftp-ingestion-connection-content"
            >
              Override connection details
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  {isEdit
                    ? "Leave blank to keep the current values. Stored credentials are never shown."
                    : "Leave blank to reuse the existing credentials from the newest SFTP server."}
                </Typography>
                {CONNECTION_FIELDS.map((field) =>
                  textField(field.name, field.label, { type: field.type }),
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          loading={isSaving}
          disabled={isSaving}
        >
          {isEdit ? "Save changes" : "Add ingestion"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SftpIngestionDialog;
